/**
 * PlayersTab — shared between operator and cashier dashboards. Lists players
 * visible to the caller's scope (via /api/cashier/players), with search,
 * activity drill, Load / Cash-out / Free-play / Edit actions, and an
 * "active in last 24h" filter.
 *
 * For cashiers under the admin's house pool (`is_house=True`), every cashier
 * sees every player — no per-cashier ownership.
 *
 * Props:
 *   showCreateButton — when true, renders a "New Player" CTA that posts to
 *                      /api/cashier/players (operator + cashier both allowed).
 *   canCashout       — controls visibility of the cashout button.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import {
    Plus, ArrowDownToLine, Search, History, UserPlus, Phone, User, Loader2, Coins,
    Gift, Pencil, Activity as ActivityIcon, HeartHandshake,
} from 'lucide-react';
import GoodwillCreditDialog from './GoodwillCreditDialog';
import PlayerActivityDialog from './PlayerActivityDialog';
import { PlayerName } from './PlayerDrilldownContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const QUICK_AMOUNTS = [20, 50, 100, 200, 500];
const FREEPLAY_QUICK = [5, 10, 25, 50, 100];

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const isActivePlayer = (p) => {
    const t = p.last_activity_at || p.last_login || p.updated_at;
    if (!t) return false;
    try { return (Date.now() - new Date(t).getTime()) < ONE_DAY_MS; } catch { return false; }
};

const PlayersTab = ({ showCreateButton = true, canCashout = true, onActionDone }) => {
    const [q, setQ] = useState('');
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeOnly, setActiveOnly] = useState(false);
    const [selected, setSelected] = useState(null);
    const [mode, setMode] = useState(null);        // 'load' | 'cashout' | 'freeplay'
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [newP, setNewP] = useState({ name: '', phone: '', password: '' });
    const [editing, setEditing] = useState(null);
    // Cashier-scoped password reset (within the cashier's operator; enforced server-side)
    const [pwVal, setPwVal] = useState('');
    const [pwBusy, setPwBusy] = useState(false);
    const [pwSet, setPwSet] = useState('');   // last value set, shown for copy
    const [activityPlayerId, setActivityPlayerId] = useState(null);

    // Reset password fields whenever a different customer is opened/closed.
    useEffect(() => { setPwVal(''); setPwSet(''); setPwBusy(false); }, [editing?.id]);

    const resetCustomerPassword = async () => {
        const val = (pwVal || '').trim();
        if (val.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        setPwBusy(true);
        try {
            await axios.post(`${API}/staff/set-player-password`, {
                user_id: editing.id, new_password: val,
            });
            setPwSet(val);
            let copied = false;
            try { await navigator.clipboard.writeText(val); copied = true; } catch { /* clipboard blocked */ }
            toast.success(copied
                ? `Password set & copied — send it to the player`
                : `Password set: ${val} — copy & send to player`,
                { duration: 15000 });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not set password');
        } finally { setPwBusy(false); }
    };
    const timer = useRef(null);

    const load = useCallback(async (queryText) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/cashier/players`, {
                params: { q: queryText }, withCredentials: true,
            });
            setPlayers(data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Search failed');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => load(q), 250);
        return () => clearTimeout(timer.current);
    }, [q, load]);

    const [paymentMethod, setPaymentMethod] = useState('');
    const [isComp, setIsComp] = useState(false);
    const [isLoan, setIsLoan] = useState(false);
    const PAYMENT_METHODS = ['Cash', 'Cash App', 'Chime', 'PayPal', 'Venmo', 'Zelle', 'Apple Pay', 'Other'];

    const openAction = (p, m) => { setSelected(p); setMode(m); setAmount(''); setNote(''); setPaymentMethod(''); setIsComp(false); setIsLoan(false); };
    const closeAction = () => { setSelected(null); setMode(null); setPaymentMethod(''); setIsComp(false); setIsLoan(false); };

    const submit = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
        if ((mode === 'load' || mode === 'cashout') && !paymentMethod) {
            toast.error(mode === 'load' ? 'Pick how the customer paid you' : 'Pick how the customer is being paid');
            return;
        }
        setBusy(true);
        try {
            const endpoint = mode === 'load' ? '/cashier/load'
                : mode === 'freeplay' ? '/cashier/freeplay'
                : '/cashier/cashout';
            const body = { player_id: selected.id, amount: amt, note: note.trim() || undefined };
            if (mode === 'load' && paymentMethod) body.payment_method = paymentMethod;
            if (mode === 'load') { body.is_comp = isComp && !isLoan; body.is_loan = isLoan; }
            if (mode === 'cashout' && paymentMethod) body.payout_method = paymentMethod;
            const { data } = await axios.post(`${API}${endpoint}`,
                body, { withCredentials: true });
            toast.success(data.message);
            closeAction();
            load(q);
            onActionDone?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Action failed');
        } finally { setBusy(false); }
    };

    const submitCreate = async () => {
        if (!newP.name.trim() || !newP.phone.trim() || newP.password.length < 4) {
            toast.error('Name, phone, and 4+ char password'); return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/cashier/players`, {
                name: newP.name.trim(), phone: newP.phone.trim(), password: newP.password,
            }, { withCredentials: true });
            toast.success(`Created ${newP.name.trim()}`);
            setCreateOpen(false);
            setNewP({ name: '', phone: '', password: '' });
            load(q);
            onActionDone?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Create failed');
        } finally { setBusy(false); }
    };

    const submitEdit = async () => {
        if (!editing) return;
        if (!editing.name?.trim()) { toast.error('Name is required'); return; }
        setBusy(true);
        try {
            await axios.put(`${API}/cashier/players/${editing.id}`, {
                name: editing.name.trim(),
                phone: (editing.phone || '').trim() || null,
                status: editing.status,
                note: (editing.staff_note || '').trim() || null,
            }, { withCredentials: true });
            toast.success(`Updated ${editing.name.trim()}`);
            setEditing(null);
            load(q);
            onActionDone?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Update failed');
        } finally { setBusy(false); }
    };

    const filteredPlayers = activeOnly ? players.filter(isActivePlayer) : players;

    return (
        <div data-testid="players-tab">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Find a player by name or phone…"
                        className="pl-10 bg-zinc-950 border-zinc-800 text-zinc-100 h-10"
                        data-testid="players-search" />
                </div>
                <div className="flex items-center gap-2 px-3 h-10 rounded-md border border-zinc-800 bg-zinc-950"
                    title="Show only players active in the last 24 hours">
                    <ActivityIcon className={`w-3.5 h-3.5 ${activeOnly ? 'text-emerald-300' : 'text-zinc-500'}`} />
                    <span className={`text-xs ${activeOnly ? 'text-emerald-300' : 'text-zinc-400'}`}>Active</span>
                    <Switch checked={activeOnly} onCheckedChange={setActiveOnly}
                        className="scale-75" data-testid="players-active-toggle" />
                </div>
                {showCreateButton && (
                    <Button onClick={() => setCreateOpen(true)} size="sm"
                        className="h-10 bg-zinc-100 text-zinc-900 hover:bg-white"
                        data-testid="players-new">
                        <UserPlus className="w-4 h-4 mr-1.5" /> New player
                    </Button>
                )}
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-900/30">
                {loading && filteredPlayers.length === 0 ? (
                    <div className="text-center py-10 text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Searching…
                    </div>
                ) : filteredPlayers.length === 0 ? (
                    <div className="text-center py-10 text-zinc-500 text-sm">
                        {activeOnly ? 'No players were active in the last 24 hours.'
                            : q.trim() ? `No players match "${q}".`
                            : 'No players in your roster yet.'}
                    </div>
                ) : (
                    <div className="divide-y divide-zinc-800">
                        {filteredPlayers.map((p, i) => (
                            <div key={p.id}
                                className="px-3 sm:px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
                                data-testid={`players-row-${i}`}>
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold text-white truncate flex items-center gap-2 text-base sm:text-lg">
                                        <User className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                                        <PlayerName id={p.id} className="truncate text-white" testIdPrefix="players-name">
                                            {p.name || 'Player'}
                                        </PlayerName>
                                        {p.status === 'banned' && (
                                            <span className="text-[10px] uppercase font-medium text-rose-300 bg-rose-500/10 border border-rose-500/20 px-1.5 py-0.5 rounded flex-shrink-0">banned</span>
                                        )}
                                        {p.status === 'suspended' && (
                                            <span className="text-[10px] uppercase font-medium text-amber-300 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded flex-shrink-0">suspended</span>
                                        )}
                                        {isActivePlayer(p) && (
                                            <span className="text-[10px] uppercase font-medium text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded flex-shrink-0">active</span>
                                        )}
                                    </p>
                                    <p className="text-xs text-zinc-500 flex items-center flex-wrap gap-x-3 gap-y-1 mt-1">
                                        {p.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span>}
                                        <span className="text-zinc-100 font-mono font-semibold text-sm">{fmt(p.credits)}</span>
                                        {p.bonus_credits > 0 && (
                                            <span className="text-amber-300 font-mono text-[11px]">+ {fmt(p.bonus_credits)} free</span>
                                        )}
                                    </p>
                                </div>
                                <div className="flex items-center justify-between sm:justify-end gap-1.5 flex-shrink-0">
                                    <Button size="sm" variant="ghost"
                                        onClick={() => setActivityPlayerId(p.id)}
                                        className="h-9 w-9 p-0 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
                                        title="View activity"
                                        data-testid={`players-activity-${i}`}>
                                        <History className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost"
                                        onClick={() => setEditing({
                                            id: p.id, name: p.name || '', phone: p.phone || '',
                                            status: p.status || 'active', staff_note: p.staff_note || '',
                                        })}
                                        className="h-9 w-9 p-0 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
                                        title="Edit customer"
                                        data-testid={`players-edit-${i}`}>
                                        <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost"
                                        onClick={() => openAction(p, 'freeplay')}
                                        disabled={p.status === 'banned'}
                                        className="h-9 w-9 p-0 text-amber-300 hover:text-amber-200 hover:bg-amber-500/10"
                                        title="Grant free play"
                                        data-testid={`players-freeplay-${i}`}>
                                        <Gift className="w-4 h-4" />
                                    </Button>
                                    <GoodwillCreditDialog player={p} onDone={() => { load(q); onActionDone?.(); }} trigger={
                                        <Button size="sm" variant="ghost" disabled={p.status === 'banned'}
                                            className="h-9 w-9 p-0 text-pink-300 hover:text-pink-200 hover:bg-pink-500/10"
                                            title="Goodwill Free Play (up to $25)"
                                            data-testid={`players-goodwill-${i}`}>
                                            <HeartHandshake className="w-4 h-4" />
                                        </Button>
                                    } />
                                    <Button size="sm" onClick={() => openAction(p, 'load')}
                                        disabled={p.status === 'banned'}
                                        className="h-9 px-3 bg-zinc-100 text-zinc-900 hover:bg-white text-xs font-semibold"
                                        data-testid={`players-load-${i}`}>
                                        <Plus className="w-3.5 h-3.5 sm:mr-1" />
                                        <span className="hidden sm:inline">Load</span>
                                    </Button>
                                    {canCashout && (
                                        <Button size="sm" variant="outline"
                                            onClick={() => openAction(p, 'cashout')}
                                            disabled={(p.credits || 0) <= 0}
                                            className="h-9 px-3 border-zinc-800 bg-zinc-900 text-zinc-200 hover:bg-zinc-800 text-xs"
                                            data-testid={`players-cashout-${i}`}>
                                            <ArrowDownToLine className="w-3.5 h-3.5 sm:mr-1" />
                                            <span className="hidden sm:inline">Out</span>
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Action dialog */}
            <Dialog open={!!selected} onOpenChange={(o) => !o && closeAction()}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md text-zinc-100" data-testid="players-action-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-zinc-100 text-xl">
                            {mode === 'load' ? <><Coins className="w-5 h-5" /> Load credits</>
                                : mode === 'freeplay' ? <><Gift className="w-5 h-5 text-amber-300" /> Grant free play</>
                                : <><ArrowDownToLine className="w-5 h-5" /> Cash out</>}
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            {selected?.name} · current balance <span className="text-zinc-100 font-semibold">{fmt(selected?.credits)}</span>
                            {mode === 'freeplay' && (
                                <span className="block text-amber-300/80 text-[11px] mt-1">
                                    Free play is playable but not cashable. Cap per issue: $500.
                                </span>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="block text-xs uppercase tracking-widest text-zinc-400 mb-2">Amount</Label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-xl">$</span>
                                <Input type="number" inputMode="decimal" min="0.01" step="0.01"
                                    value={amount} onChange={(e) => setAmount(e.target.value)}
                                    className="pl-7 bg-zinc-950 border-zinc-800 text-zinc-100 text-3xl font-mono font-semibold h-16"
                                    data-testid="players-amount" autoFocus />
                            </div>
                            <div className="flex gap-2 mt-2">
                                {(mode === 'freeplay' ? FREEPLAY_QUICK : QUICK_AMOUNTS).map((qq) => (
                                    <button key={qq} type="button" onClick={() => setAmount(String(qq))}
                                        className="flex-1 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-300 text-xs hover:bg-zinc-800"
                                        data-testid={`players-quick-${qq}`}>${qq}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="block text-xs uppercase tracking-widest text-zinc-400 mb-2">Note (optional)</Label>
                            <Input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                                placeholder={mode === 'freeplay' ? 'e.g. birthday gift' : 'e.g. paid in cash'}
                                className="bg-zinc-950 border-zinc-800 text-zinc-100"
                                data-testid="players-note" />
                        </div>
                        {(mode === 'load' || mode === 'cashout') && (
                            <div>
                                <Label className="block text-xs uppercase tracking-widest text-zinc-400 mb-2">
                                    {mode === 'load' ? 'How did the customer pay you?' : 'How is the customer being paid?'}
                                </Label>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                    {PAYMENT_METHODS.map(m => (
                                        <button key={m} type="button"
                                            onClick={() => setPaymentMethod(m)}
                                            className={`py-2.5 rounded-md text-xs font-bold border transition ${
                                                paymentMethod === m
                                                    ? 'bg-emerald-500 border-emerald-400 text-zinc-900 shadow-md shadow-emerald-500/20'
                                                    : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-zinc-800'
                                            }`}
                                            data-testid={`players-paymethod-${m.replace(/\s+/g, '-').toLowerCase()}`}>
                                            {m}
                                        </button>
                                    ))}
                                </div>
                                {!paymentMethod && (
                                    <p className="text-[10px] text-amber-300/80 mt-2">
                                        Pick one — this is how we tag where each dollar moved at end of day.
                                    </p>
                                )}
                            </div>
                        )}
                        {mode === 'load' && (
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 cursor-pointer text-xs text-zinc-400" data-testid="players-comp-toggle">
                                    <input
                                        type="checkbox"
                                        checked={isComp}
                                        onChange={(e) => { setIsComp(e.target.checked); if (e.target.checked) setIsLoan(false); }}
                                        className="w-4 h-4 rounded border-zinc-700 bg-zinc-950 text-violet-500 focus:ring-violet-500"
                                        data-testid="players-comp-checkbox"
                                    />
                                    <span>
                                        <span className={`font-bold ${isComp ? 'text-violet-400' : 'text-zinc-200'}`}>Comp / no cash taken</span>
                                        <span className="block text-[10px] text-zinc-500">Tracks separately in Reports — skips daily bonus & referral.</span>
                                    </span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer text-xs text-zinc-400" data-testid="players-loan-toggle">
                                    <input
                                        type="checkbox"
                                        checked={isLoan}
                                        onChange={(e) => { setIsLoan(e.target.checked); if (e.target.checked) setIsComp(false); }}
                                        className="w-4 h-4 rounded border-zinc-700 bg-zinc-950 text-orange-500 focus:ring-orange-500"
                                        data-testid="players-loan-checkbox"
                                    />
                                    <span>
                                        <span className={`font-bold ${isLoan ? 'text-orange-400' : 'text-zinc-200'}`}>Loan / owed back</span>
                                        <span className="block text-[10px] text-zinc-500">Player owes this back — shows in Reports → Loans bucket with who got it.</span>
                                    </span>
                                </label>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={closeAction} disabled={busy}
                            className="border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
                        <Button onClick={submit} disabled={busy || !amount || parseFloat(amount) <= 0}
                            className={mode === 'freeplay' ? 'bg-amber-400 text-zinc-900 hover:bg-amber-300' : 'bg-zinc-100 text-zinc-900 hover:bg-white'}
                            data-testid="players-confirm">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                                <>{mode === 'load' ? 'Load' : mode === 'freeplay' ? 'Grant' : 'Cash out'} {fmt(parseFloat(amount) || 0)}</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit player dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md text-zinc-100" data-testid="players-edit-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-zinc-100 flex items-center gap-2">
                            <Pencil className="w-4 h-4" /> Edit customer
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            You can change basic contact info, status, and reset the customer's password.
                        </DialogDescription>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3 py-2">
                            <div>
                                <Label className="text-zinc-300 text-xs">Name</Label>
                                <Input value={editing.name}
                                    onChange={(e) => setEditing(p => ({ ...p, name: e.target.value }))}
                                    className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100"
                                    data-testid="players-edit-name" />
                            </div>
                            <div>
                                <Label className="text-zinc-300 text-xs">Phone</Label>
                                <Input value={editing.phone}
                                    onChange={(e) => setEditing(p => ({ ...p, phone: e.target.value }))}
                                    className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100"
                                    data-testid="players-edit-phone" />
                            </div>
                            <div>
                                <Label className="text-zinc-300 text-xs">Status</Label>
                                <div className="flex items-center gap-2 mt-1">
                                    {['active', 'suspended', 'banned'].map(s => (
                                        <button key={s} type="button"
                                            onClick={() => setEditing(p => ({ ...p, status: s }))}
                                            className={`flex-1 py-2 rounded-md border text-xs ${
                                                editing.status === s
                                                    ? (s === 'active' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                                                        : s === 'suspended' ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                                                        : 'border-rose-500/40 bg-rose-500/10 text-rose-200')
                                                    : 'border-zinc-800 bg-zinc-950 text-zinc-500 hover:text-zinc-300'
                                            }`}
                                            data-testid={`players-edit-status-${s}`}>{s}</button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-300 text-xs">Staff note (private)</Label>
                                <Input value={editing.staff_note}
                                    onChange={(e) => setEditing(p => ({ ...p, staff_note: e.target.value }))}
                                    placeholder="e.g. prefers cash, VIP, comes Fridays…"
                                    className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100"
                                    data-testid="players-edit-note" />
                            </div>
                            <div className="pt-3 border-t border-zinc-800">
                                <Label className="text-zinc-300 text-xs">Reset password</Label>
                                <div className="flex items-center gap-2 mt-1">
                                    <Input
                                        type="text"
                                        value={pwVal}
                                        onChange={(e) => setPwVal(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') resetCustomerPassword(); }}
                                        placeholder="New password (min 6 chars)"
                                        className="bg-zinc-950 border-zinc-800 text-zinc-100"
                                        data-testid="players-edit-password-input" />
                                    <Button type="button" onClick={resetCustomerPassword} disabled={pwBusy}
                                        className="bg-amber-400 text-black hover:bg-amber-300 font-semibold whitespace-nowrap"
                                        data-testid="players-edit-password-set">
                                        {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set'}
                                    </Button>
                                </div>
                                {pwSet && (
                                    <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1.5"
                                         data-testid="players-edit-password-result">
                                        <span className="text-xs text-emerald-200">New password: <b className="font-mono">{pwSet}</b></span>
                                        <button type="button"
                                            onClick={async () => { try { await navigator.clipboard.writeText(pwSet); toast.success('Copied'); } catch { toast.message(`Password: ${pwSet}`); } }}
                                            className="text-emerald-300 hover:text-white text-xs underline"
                                            data-testid="players-edit-password-copy">Copy</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}
                            className="border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
                        <Button onClick={submitEdit} disabled={busy}
                            className="bg-zinc-100 text-zinc-900 hover:bg-white"
                            data-testid="players-edit-save">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md text-zinc-100" data-testid="players-create-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-zinc-100">New player</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            They&apos;ll log in with phone + password.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-zinc-300 text-xs">Name</Label>
                            <Input value={newP.name} onChange={(e) => setNewP({ ...newP, name: e.target.value })}
                                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="players-new-name" />
                        </div>
                        <div>
                            <Label className="text-zinc-300 text-xs">Phone</Label>
                            <Input value={newP.phone} onChange={(e) => setNewP({ ...newP, phone: e.target.value })}
                                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="players-new-phone" />
                        </div>
                        <div>
                            <Label className="text-zinc-300 text-xs">Password</Label>
                            <Input type="password" value={newP.password}
                                onChange={(e) => setNewP({ ...newP, password: e.target.value })}
                                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="players-new-password" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)}
                            className="border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
                        <Button onClick={submitCreate} disabled={busy}
                            className="bg-zinc-100 text-zinc-900 hover:bg-white" data-testid="players-confirm-create">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create player'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <PlayerActivityDialog
                playerId={activityPlayerId}
                open={!!activityPlayerId}
                onClose={() => setActivityPlayerId(null)}
            />
        </div>
    );
};

export default PlayersTab;
